import type { AnalyticsGranularity } from "@situate/contracts/runtime";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface AnalyticsQueryRange {
  granularity: AnalyticsGranularity;
  startTime: string;
  endTime: string;
}

/**
 * Maps start and end dates from the Analytics page filter to the time range and bucket
 * granularity used to query `situate_analytics`-backed runtime API endpoints
 * (e.g. `area-population`, `area-density`, `area-throughput`).
 *
 * Granularities are chosen to keep data point counts reasonable:
 * - 1-2 days: 10s granularity (~8,640-17,280 points)
 * - 3-60 days: hourly granularity (72-1,440 points)
 * - 61+ days: daily granularity
 */
export function getAnalyticsQueryRange(startDate: Date, endDate: Date): AnalyticsQueryRange {
  const startTime = startDate.toISOString();
  const endTime = endDate.toISOString();
  const rangeDays = (endDate.getTime() - startDate.getTime()) / DAY_MS;

  let granularity: AnalyticsGranularity;
  if (rangeDays <= 2) {
    granularity = "10s";
  } else if (rangeDays <= 60) {
    granularity = "hourly";
  } else {
    granularity = "daily";
  }

  return { granularity, startTime, endTime };
}

/**
 * Uses the same bucket selection as Total Population Trend for every Analytics chart.
 */
export function getTotalPopulationTrendQueryRange(
  startDate: Date,
  endDate: Date
): AnalyticsQueryRange {
  const rangeMs = endDate.getTime() - startDate.getTime();
  const hourMs = 60 * 60 * 1000;
  let granularity: AnalyticsGranularity;

  if (rangeMs <= 6 * hourMs) {
    granularity = "60s";
  } else if (rangeMs <= DAY_MS) {
    granularity = "120s";
  } else if (rangeMs <= 60 * DAY_MS) {
    granularity = "hourly";
  } else {
    granularity = "daily";
  }

  return { granularity, startTime: startDate.toISOString(), endTime: endDate.toISOString() };
}

/**
 * Returns the expected bucket timestamps for an analytics query. Missing API buckets can be
 * represented explicitly with null values so charts render an unavailable range instead of
 * silently compressing the time axis.
 */
export function getAnalyticsBucketTimestamps(
  startDate: Date,
  endDate: Date,
  granularity: AnalyticsGranularity
): string[] {
  const durationMs =
    granularity === "10s"
      ? 10_000
      : granularity === "30s"
        ? 30_000
        : granularity === "60s"
          ? 60_000
          : granularity === "120s"
            ? 120_000
            : granularity === "hourly"
              ? 60 * 60 * 1000
              : 24 * 60 * 60 * 1000;
  const timestamps: string[] = [];
  for (
    let timestamp = Math.floor(startDate.getTime() / durationMs) * durationMs;
    timestamp < endDate.getTime();
    timestamp += durationMs
  ) {
    timestamps.push(new Date(timestamp).toISOString());
  }
  return timestamps;
}

/** Formats a data point's timestamp as an analytics chart's x-axis period label. */
export function formatAnalyticsPeriodLabel(
  timestamp: string,
  granularity: AnalyticsGranularity
): string {
  const date = new Date(timestamp);

  if (granularity === "10s" || granularity === "30s") {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  }

  if (granularity === "60s" || granularity === "120s") {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }

  if (granularity === "hourly") {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
    }).format(date);
  }

  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}
